import type { DynamicInvoiceMeasurementChartMeter } from '@weft/api-definition';
import { isPerUnitUsageProductOnly, isTieredSubscriptionUsageProductOnly, isTiersUsageProductOnly } from '@weft/services';
import type { ProductLineItem } from '@weft/services';
import type { LineItemProduct } from '@weft/types';
import type { Optional } from '@weft/utils';
import { isNotEmptyString } from '@weft/utils';
import { raise } from '@weft/utils';
import React, { useState } from 'react';
import { InstanceKeyValueSelector } from './InstanceKeyValueSelector';
import { PerUnitUsageLineItemBody } from './PerUnitUsageLineItemBody';
import { TieredSubscriptionUsageLineItemBody } from './TieredSubscriptionUsageLineItemBody';
import { TiersUsageLineItemBody } from './TiersUsageLineItemBody';

type UsageLineItemBodyProps = {
  productLineItem: ProductLineItem;
  productMetersMeasurementChartData: Optional<DynamicInvoiceMeasurementChartMeter>,
  lineItemProduct: LineItemProduct,
  units: number;
  customerId: string;
};

const UsageLineItemBody: React.FC<UsageLineItemBodyProps> = ({
  productLineItem,
  lineItemProduct,
  customerId,
  productMetersMeasurementChartData,
}) => {
  const [selectedInstanceKeyValue, setSelectedInstanceKeyValue] = useState<string>();
  const instanceKey = productLineItem.lineItemProduct.variants[0].meter?.aggregationMethod.instanceKey;
  const isInstanceProduct = isNotEmptyString(instanceKey);
  const createUsageLineItem = useUsageLineItemFactory();

  return (
    <div className="py-7.5 pl-6.5 flex flex-col gap-5 pr-4">
      {isInstanceProduct && (
        <InstanceKeyValueSelector
          productId={productLineItem.lineItemProduct.productId}
          customerId={customerId}
          instanceKey={instanceKey}
          onValueSelected={(instanceKeyValue?: string ) => setSelectedInstanceKeyValue(instanceKeyValue)}
        />
      )}
      {createUsageLineItem(productLineItem, lineItemProduct, selectedInstanceKeyValue, productMetersMeasurementChartData)}
    </div>
  );
};

const MemoizedUsageLineItemBody = React.memo(UsageLineItemBody);

export { MemoizedUsageLineItemBody as UsageLineItemBody };

function useUsageLineItemFactory() {
  return (productLineItem: ProductLineItem, lineItemProduct: LineItemProduct, selectedInstanceKeyValue: string | undefined, productMetersMeasurementChartData: Optional<DynamicInvoiceMeasurementChartMeter>) => {
    if (isPerUnitUsageProductOnly(lineItemProduct)) {
      return (
        <PerUnitUsageLineItemBody {...{ lineItemProduct, productLineItem, productMetersMeasurementChartData, selectedInstanceKeyValue }}/>
      );
    }

    if (isTiersUsageProductOnly(lineItemProduct)) {
      return <TiersUsageLineItemBody {...{ lineItemProduct, productLineItem, productMetersMeasurementChartData, selectedInstanceKeyValue }}/>;
    }

    if (isTieredSubscriptionUsageProductOnly(lineItemProduct)) {
      return <TieredSubscriptionUsageLineItemBody {...{ lineItemProduct, productLineItem, productMetersMeasurementChartData, selectedInstanceKeyValue }}/>;
    }

    return raise(`Unknown usage product type: ${JSON.stringify(lineItemProduct, null, 2)}`);
  };
}